import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'motion/react';
import { useSwipeable } from 'react-swipeable';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import axios from 'axios';
import { getApiUrl } from '../apiUrl';
import UserIdentity from './UserIdentity';

const EventImageSlideshow = ({
  photos,
  initialIndex = 0,
  getImageUrl,
  onClose,
  eventId,
  poster = null,
  updateIndex = null,
  alreadyHelpful = false,
  description,
  onHelpful
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  // Photos in one slideshow can come from different posters, so votes and
  // credited counts are tracked per post rather than for the slideshow.
  const [votedPosts, setVotedPosts] = useState({});
  const [posterOverrides, setPosterOverrides] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const containerRef = useRef(null);

  const API_URL = getApiUrl();

  const currentPhoto = photos?.[currentIndex];
  const photoPoster = currentPhoto?._poster ?? poster;
  const photoUpdateIndex = currentPhoto?._updateIndex ?? updateIndex;
  const photoDescription = currentPhoto?._description ?? description;
  const postKey = photoUpdateIndex === null || photoUpdateIndex === undefined
    ? 'event'
    : `update:${photoUpdateIndex}`;

  const isHelpful = votedPosts[postKey] || !!(currentPhoto?._alreadyHelpful ?? alreadyHelpful);
  const displayPoster = posterOverrides[postKey] || photoPoster;

  const handleHelpful = async () => {
    if (!eventId || isHelpful || submitting) return;
    setSubmitting(true);

    // Optimistic: mark voted and bump this poster's helpful count
    setVotedPosts((prev) => ({ ...prev, [postKey]: true }));
    if (photoPoster && typeof photoPoster === 'object') {
      setPosterOverrides((prev) => ({
        ...prev,
        [postKey]: { ...photoPoster, helpfulCount: (photoPoster.helpfulCount || 0) + 1 }
      }));
    }

    try {
      const isUpdate = photoUpdateIndex !== null && photoUpdateIndex !== undefined;
      const response = isUpdate
        ? await axios.post(
            `${API_URL}/api/events/${eventId}/updates/${photoUpdateIndex}/vote`,
            { isHelpful: true },
            { withCredentials: true }
          )
        : await axios.post(
            `${API_URL}/api/events/${eventId}/helpful`,
            {},
            { withCredentials: true }
          );

      if (response.data?.success) {
        const nextEvent = response.data.event || null;
        const serverPoster = isUpdate
          ? nextEvent?.updates?.[photoUpdateIndex]?.userId
          : nextEvent?.userId;
        if (serverPoster && typeof serverPoster === 'object') {
          setPosterOverrides((prev) => ({ ...prev, [postKey]: serverPoster }));
        }
        if (onHelpful) {
          const targetUserId = typeof photoPoster === 'object' ? photoPoster?._id : photoPoster;
          onHelpful(nextEvent, targetUserId);
        }
      }
    } catch (error) {
      console.error('Error marking helpful:', error);
      setVotedPosts((prev) => {
        const next = { ...prev };
        delete next[postKey];
        return next;
      });
      setPosterOverrides((prev) => {
        const next = { ...prev };
        delete next[postKey];
        return next;
      });
      alert('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (currentIndex < photos.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    },
    onSwipedRight: () => {
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    },
    trackMouse: true
  });

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, onClose]);

  if (!photos || photos.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 10004,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onClose}
      >
        <Box
          {...handlers}
          ref={containerRef}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '100vw',
            maxHeight: '100vh'
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10000,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>

          {currentIndex > 0 && (
            <IconButton
              onClick={goToPrevious}
              sx={{
                position: 'absolute',
                left: 16,
                zIndex: 10000,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>
          )}

          {currentIndex < photos.length - 1 && (
            <IconButton
              onClick={goToNext}
              sx={{
                position: 'absolute',
                right: 16,
                zIndex: 10000,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.3 }}
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                src={getImageUrl(currentPhoto.url || currentPhoto)}
                alt={`Photo ${currentIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </motion.div>
          </AnimatePresence>

          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              zIndex: 10000
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <UserIdentity user={displayPoster} size="md" variant="dark" />
              {photoDescription && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255,255,255,0.9)',
                    mt: 0.75,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {photoDescription}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                onClick={handleHelpful}
                disabled={isHelpful || submitting}
                aria-label="Mark helpful"
                sx={{
                  color: isHelpful ? '#81c784' : 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  '&.Mui-disabled': { color: '#81c784' }
                }}
              >
                <ThumbUpIcon />
              </IconButton>
            </Box>
          </Box>

          {photos.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                zIndex: 10000
              }}
            >
              <Typography variant="caption" sx={{ color: 'white' }}>
                {currentIndex + 1} / {photos.length}
              </Typography>
            </Box>
          )}
        </Box>
      </motion.div>
    </AnimatePresence>
  );
};

export default EventImageSlideshow;
